import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  BarChart3,
  Clock3,
  Database,
  Disc3,
  Eye,
  Users,
  ExternalLink,
  Heart,
  ListMusic,
  MessageCircle,
  Music2,
  RefreshCw,
  TrendingUp,
  X,
} from "lucide-react";

type Range = "24h" | "7d" | "30d" | "90d" | "all";
type SortKey = "views" | "likes" | "comments" | "rank" | "position" | "added" | "title";
type Direction = "asc" | "desc";

interface PlaylistSnapshot {
  capturedAt: string;
  videoCount: number;
  playlistItemCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalFavorites: number;
  averageViews: number;
}

interface Playlist {
  id: string;
  title: string;
  description: string | null;
  channelId: string | null;
  channelTitle: string | null;
  publishedAt: string | null;
  privacyStatus: string | null;
  itemCount: number;
  thumbnailUrl: string | null;
  lastSyncedAt: string;
}

interface VideoLatest {
  capturedAt: string;
  viewCount: number | null;
  likeCount: number | null;
  favoriteCount: number | null;
  commentCount: number | null;
  rankByViews: number | null;
  rankByLikes: number | null;
  rankByComments: number | null;
  playlistPosition: number | null;
}

interface Video {
  id: string;
  title: string;
  description: string | null;
  channelTitle: string | null;
  videoPublishedAt: string | null;
  playlistAddedAt: string | null;
  currentPlaylistPosition: number | null;
  thumbnailUrl: string | null;
  duration: string | null;
  definition: string | null;
  dimension: string | null;
  captionStatus: string | null;
  licensedContent: boolean | null;
  madeForKids: boolean | null;
  embeddable: boolean | null;
  privacyStatus: string | null;
  publicStatsViewable: boolean | null;
  categoryId: string | null;
  liveBroadcastContent: string | null;
  topicCategories: string[];
  recordingDate: string | null;
  scheduledStartTime: string | null;
  actualStartTime: string | null;
  actualEndTime: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  removedAt: string | null;
  latest: VideoLatest | null;
}

interface ChannelGroup {
  name: string;
  coverUrl: string | null;
  firstVideoTitle: string;
  videoCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  averageViews: number;
}

interface ChannelGroupHistoryPoint {
  capturedAt: string;
  videoCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalFavorites: number;
  averageViews: number;
}

interface ChannelGroupDetail {
  range: Range;
  group: ChannelGroup;
  history: ChannelGroupHistoryPoint[];
  videos: Video[];
}

interface RunStatus {
  id: string;
  trigger: "cron" | "manual";
  startedAt: string;
  completedAt: string | null;
  status: "running" | "succeeded" | "failed";
  videoCount: number;
  playlistItemCount: number;
  error: string | null;
}

interface ChannelSubscriberPoint {
  capturedAt: string;
  subscriberCount: number | null;
}

interface ChannelSubscribers {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  subscriberCount: number | null;
  hiddenSubscriberCount: boolean;
  viewCount: number | null;
  videoCount: number | null;
  capturedAt: string;
  history: ChannelSubscriberPoint[];
}

interface Dashboard {
  range: Range;
  playlist: Playlist | null;
  latest: PlaylistSnapshot | null;
  baseline: PlaylistSnapshot | null;
  timeline: PlaylistSnapshot[];
  channels: ChannelSubscribers[];
  topVideos: Video[];
  latestRun: RunStatus | null;
  syncIntervalHours: number;
}

const channelColors = ["#a78bfa", "#22d3ee", "#f472b6", "#fbbf24", "#34d399", "#fb7185", "#60a5fa", "#c084fc"];

interface VideoHistoryPoint {
  capturedAt: string;
  viewCount: number | null;
  likeCount: number | null;
  favoriteCount: number | null;
  commentCount: number | null;
  rankByViews: number | null;
  rankByLikes: number | null;
  rankByComments: number | null;
  playlistPosition: number | null;
}

interface VideoDetail {
  range: Range;
  video: Video;
  history: VideoHistoryPoint[];
}

const rangeLabels: Record<Range, string> = {
  "24h": "24 saat",
  "7d": "7 gün",
  "30d": "30 gün",
  "90d": "90 gün",
  all: "Tüm zamanlar",
};

const numberFormatter = new Intl.NumberFormat("tr-TR");
const compactFormatter = new Intl.NumberFormat("tr-TR", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const dateFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});
const shortDateFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
});
const dateTimeFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function formatNumber(value: number | null | undefined, compact = false): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return (compact ? compactFormatter : numberFormatter).format(value);
}

function formatDate(value: string | null, includeTime = false): string {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : (includeTime ? dateTimeFormatter : dateFormatter).format(date);
}

function formatDuration(value: string | null): string {
  if (!value) {
    return "—";
  }
  const match = value.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!match) {
    return value;
  }
  const [, hours = "0", minutes = "0", seconds = "0"] = match;
  const parts = Number(hours) ? [hours.padStart(2, "0"), minutes.padStart(2, "0"), seconds.padStart(2, "0")] : [minutes, seconds.padStart(2, "0")];
  return parts.join(":");
}

function shortTitle(value: string, max = 28): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function difference(current: number | null | undefined, baseline: number | null | undefined): number | null {
  if (current === null || current === undefined || baseline === null || baseline === undefined) {
    return null;
  }
  return current - baseline;
}

function youtubeVideoUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

function groupVideosByChannel(videos: Video[]): ChannelGroup[] {
  const videosByChannel = new Map<string, Video[]>();

  for (const video of videos) {
    const channel = video.channelTitle?.trim() || "Bilinmeyen kanal / albüm";
    videosByChannel.set(channel, [...(videosByChannel.get(channel) ?? []), video]);
  }

  return [...videosByChannel.entries()]
    .map(([name, channelVideos]) => {
      const firstVideo = [...channelVideos].sort(
        (left, right) =>
          (left.currentPlaylistPosition ?? Number.MAX_SAFE_INTEGER) -
          (right.currentPlaylistPosition ?? Number.MAX_SAFE_INTEGER),
      )[0];
      const totals = channelVideos.reduce(
        (accumulator, video) => ({
          views: accumulator.views + (video.latest?.viewCount ?? 0),
          likes: accumulator.likes + (video.latest?.likeCount ?? 0),
          comments: accumulator.comments + (video.latest?.commentCount ?? 0),
        }),
        { views: 0, likes: 0, comments: 0 },
      );

      return {
        name,
        coverUrl: firstVideo.thumbnailUrl,
        firstVideoTitle: firstVideo.title,
        videoCount: channelVideos.length,
        totalViews: totals.views,
        totalLikes: totals.likes,
        totalComments: totals.comments,
        averageViews: channelVideos.length ? Math.round(totals.views / channelVideos.length) : 0,
      };
    })
    .sort((left, right) => right.totalViews - left.totalViews || left.name.localeCompare(right.name, "tr"));
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `İstek başarısız (${response.status})`);
  }
  return (await response.json()) as T;
}

function MetricCard({
  label,
  value,
  delta,
  icon,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  delta: number | null;
  icon: ReactNode;
  tone: "violet" | "cyan" | "pink" | "amber";
}) {
  return (
    <article className={`metric-card metric-card--${tone}`}>
      <div className="metric-card__top">
        <span>{label}</span>
        <span className="metric-card__icon">{icon}</span>
      </div>
      <strong>{formatNumber(value, true)}</strong>
      <div className={`metric-card__delta ${delta === null ? "is-muted" : delta && delta < 0 ? "is-negative" : ""}`}>
        {delta === null ? (
          "İlk ölçüm bekleniyor"
        ) : (
          <>
            {delta < 0 ? <ArrowDown size={13} /> : <ArrowUp size={13} />}
            {formatNumber(Math.abs(delta))} seçili aralıkta
          </>
        )}
      </div>
    </article>
  );
}

function EmptyChart({ label }: { label: string }) {
  return (
    <div className="chart-empty">
      <Activity size={24} />
      <span>{label}</span>
    </div>
  );
}

function SortButton({
  label,
  sortKey,
  current,
  direction,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  direction: Direction;
  onSort: (value: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = current === sortKey;
  return (
    <button
      className={`sort-button sort-button--${align} ${active ? "is-active" : ""}`}
      onClick={() => onSort(sortKey)}
      type="button"
    >
      {label}
      {active && (direction === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />)}
    </button>
  );
}

function VideoAvatar({ video, size = "normal" }: { video: Video; size?: "normal" | "large" }) {
  return video.thumbnailUrl ? (
    <img className={`video-thumb video-thumb--${size}`} src={video.thumbnailUrl} alt="" loading="lazy" />
  ) : (
    <span className={`video-thumb video-thumb--${size} video-thumb--placeholder`}>
      <Music2 size={size === "large" ? 25 : 17} />
    </span>
  );
}

function AlbumCover({ group }: { group: ChannelGroup }) {
  return group.coverUrl ? (
    <img className="album-cover" src={group.coverUrl} alt={`${group.name} ilk video kapağı`} loading="lazy" />
  ) : (
    <span className="album-cover album-cover--placeholder">
      <Disc3 size={22} />
    </span>
  );
}

function DetailPanel({
  detail,
  onClose,
}: {
  detail: VideoDetail;
  onClose: () => void;
}) {
  const { video } = detail;
  const chartData = detail.history.map((point) => ({
    ...point,
    label: shortDateFormatter.format(new Date(point.capturedAt)),
  }));

  return (
    <div className="detail-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        aria-label="Video detayları"
        className="detail-panel"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="detail-panel__header">
          <div className="detail-video">
            <VideoAvatar video={video} size="large" />
            <div>
              <p className="eyebrow">Video analizi</p>
              <h2>{video.title}</h2>
              <span>{video.channelTitle || "Bilinmeyen kanal"}</span>
            </div>
          </div>
          <div className="detail-actions">
            <a
              className="icon-button"
              href={youtubeVideoUrl(video.id)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Videoyu YouTube'da yeni sekmede aç"
              title="YouTube'da aç"
            >
              <ExternalLink size={18} />
            </a>
            <button className="icon-button" type="button" onClick={onClose} aria-label="Kapat">
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="detail-metrics">
          <div>
            <span>İzlenme</span>
            <strong>{formatNumber(video.latest?.viewCount)}</strong>
          </div>
          <div>
            <span>Beğeni</span>
            <strong>{formatNumber(video.latest?.likeCount)}</strong>
          </div>
          <div>
            <span>Yorum</span>
            <strong>{formatNumber(video.latest?.commentCount)}</strong>
          </div>
          <div>
            <span>İzlenme sırası</span>
            <strong>{video.latest?.rankByViews ? `#${video.latest.rankByViews}` : "—"}</strong>
          </div>
        </div>

        <section className="detail-chart">
          <div className="section-title">
            <div>
              <p className="eyebrow">Tarihsel metrik</p>
              <h3>İzlenme büyümesi</h3>
            </div>
          </div>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={225}>
              <AreaChart data={chartData} margin={{ top: 10, right: 6, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="detailViews" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.42} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={28} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis tickFormatter={(value) => formatNumber(value, true)} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Area dataKey="viewCount" name="İzlenme" stroke="#a78bfa" fill="url(#detailViews)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Bu video için ilk tarihsel ölçüm bekleniyor." />
          )}
        </section>

        <section className="detail-chart">
          <div className="section-title">
            <div>
              <p className="eyebrow">Tarihsel etkileşim</p>
              <h3>Beğeni ve yorum geçmişi</h3>
            </div>
          </div>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={205}>
              <LineChart data={chartData} margin={{ top: 10, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={28} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis
                  yAxisId="likes"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#f9a8d4", fontSize: 11 }}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="comments"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#fcd34d", fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Line yAxisId="likes" dataKey="likeCount" name="Beğeni" stroke="#f472b6" strokeWidth={2.5} dot={{ r: 3, fill: "#f472b6" }} />
                <Line yAxisId="comments" dataKey="commentCount" name="Yorum" stroke="#fbbf24" strokeWidth={2.5} dot={{ r: 3, fill: "#fbbf24" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Beğeni ve yorum geçmişi, ilk senkronizasyondan sonra oluşur." />
          )}
        </section>

        <section className="detail-chart">
          <div className="section-title">
            <div>
              <p className="eyebrow">Liste içi rekabet</p>
              <h3>Top izlenme sırası</h3>
            </div>
          </div>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={205}>
              <LineChart data={chartData} margin={{ top: 10, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={28} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis
                  dataKey="rankByViews"
                  reversed
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#8f9bb3", fontSize: 11 }}
                  domain={["dataMin", "dataMax"]}
                />
                <Tooltip formatter={(value) => (value ? `#${value}` : "—")} />
                <Line dataKey="rankByViews" name="İzlenme sırası" stroke="#22d3ee" strokeWidth={2.5} dot={{ r: 3, fill: "#22d3ee" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sıralama geçmişi, ilk senkronizasyondan sonra oluşur." />
          )}
        </section>

        <section className="metadata-grid">
          <Metadata label="Playlist'e eklenme" value={formatDate(video.playlistAddedAt, true)} />
          <Metadata label="Yayın tarihi" value={formatDate(video.videoPublishedAt)} />
          <Metadata label="Süre" value={formatDuration(video.duration)} />
          <Metadata label="Playlist konumu" value={video.currentPlaylistPosition ? `#${video.currentPlaylistPosition}` : "—"} />
          <Metadata label="Görüntü kalitesi" value={video.definition?.toUpperCase() || "—"} />
          <Metadata label="Altyazı" value={video.captionStatus || "—"} />
          <Metadata label="Gizlilik" value={video.privacyStatus || "—"} />
          <Metadata label="Gömülebilir" value={video.embeddable === null ? "—" : video.embeddable ? "Evet" : "Hayır"} />
        </section>

        {video.topicCategories.length > 0 && (
          <section className="topic-list">
            <p className="eyebrow">YouTube topic kategorileri</p>
            <div>
              {video.topicCategories.map((topic) => (
                <span key={topic}>{topic.split("/").at(-1)?.replaceAll("_", " ")}</span>
              ))}
            </div>
          </section>
        )}
      </aside>
    </div>
  );
}

function AlbumDetailPanel({
  detail,
  onClose,
  onSelectVideo,
}: {
  detail: ChannelGroupDetail;
  onClose: () => void;
  onSelectVideo: (videoId: string) => void;
}) {
  const { group } = detail;
  const historyData = detail.history.map((point) => ({
    ...point,
    label: shortDateFormatter.format(new Date(point.capturedAt)),
  }));
  const topVideos = detail.videos.slice(0, 12).map((video) => ({
    id: video.id,
    title: shortTitle(video.title, 26),
    views: video.latest?.viewCount ?? 0,
  }));

  return (
    <div className="detail-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        aria-label="Kanal veya albüm detayları"
        className="detail-panel"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="detail-panel__header">
          <div className="detail-video">
            <AlbumCover group={group} />
            <div>
              <p className="eyebrow">KANAL / ALBÜM DETAYI</p>
              <h2>{group.name}</h2>
              <span>İlk video: {group.firstVideoTitle}</span>
            </div>
          </div>
          <div className="detail-actions">
            <button className="icon-button" type="button" onClick={onClose} aria-label="Kapat">
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="detail-metrics">
          <div>
            <span>Video</span>
            <strong>{formatNumber(group.videoCount)}</strong>
          </div>
          <div>
            <span>İzlenme</span>
            <strong>{formatNumber(group.totalViews)}</strong>
          </div>
          <div>
            <span>Beğeni</span>
            <strong>{formatNumber(group.totalLikes)}</strong>
          </div>
          <div>
            <span>Yorum</span>
            <strong>{formatNumber(group.totalComments)}</strong>
          </div>
        </div>

        <section className="detail-chart">
          <div className="section-title">
            <div>
              <p className="eyebrow">TARİHSEL TOPLAM</p>
              <h3>Albüm / kanal izlenme eğrisi</h3>
            </div>
          </div>
          {historyData.length ? (
            <ResponsiveContainer width="100%" height={225}>
              <AreaChart data={historyData} margin={{ top: 10, right: 6, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="albumDetailViews" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.42} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={28} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis tickFormatter={(value) => formatNumber(value, true)} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Area type="monotone" dataKey="totalViews" name="İzlenme" stroke="#22d3ee" fill="url(#albumDetailViews)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Bu grup için ilk tarihsel ölçüm bekleniyor." />
          )}
        </section>

        <section className="detail-chart">
          <div className="section-title">
            <div>
              <p className="eyebrow">TARİHSEL ETKİLEŞİM</p>
              <h3>Beğeni ve yorum geçmişi</h3>
            </div>
          </div>
          {historyData.length ? (
            <ResponsiveContainer width="100%" height={205}>
              <LineChart data={historyData} margin={{ top: 10, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={28} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis yAxisId="likes" tickLine={false} axisLine={false} tick={{ fill: "#f9a8d4", fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="comments" orientation="right" tickLine={false} axisLine={false} tick={{ fill: "#fcd34d", fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Line yAxisId="likes" dataKey="totalLikes" name="Beğeni" stroke="#f472b6" strokeWidth={2.5} dot={{ r: 3, fill: "#f472b6" }} />
                <Line yAxisId="comments" dataKey="totalComments" name="Yorum" stroke="#fbbf24" strokeWidth={2.5} dot={{ r: 3, fill: "#fbbf24" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Etkileşim geçmişi, senkronizasyonlarla oluşur." />
          )}
        </section>

        <section className="detail-chart">
          <div className="section-title">
            <div>
              <p className="eyebrow">VİDEO DAĞILIMI</p>
              <h3>Grubun en çok izlenen videoları</h3>
            </div>
          </div>
          {topVideos.length ? (
            <ResponsiveContainer width="100%" height={Math.max(210, topVideos.length * 36)}>
              <BarChart data={topVideos} layout="vertical" margin={{ top: 4, right: 10, left: 12, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="title" type="category" width={150} tickLine={false} axisLine={false} tick={{ fill: "#b8c0d3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Bar dataKey="views" name="İzlenme" fill="#a78bfa" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Grup videoları henüz hazır değil." />
          )}
        </section>

        <section className="group-video-list">
          <p className="eyebrow">GRUP VİDEOLARI</p>
          {detail.videos.map((video) => (
            <button key={video.id} type="button" onClick={() => onSelectVideo(video.id)}>
              <span>{video.title}</span>
              <strong>{formatNumber(video.latest?.viewCount)}</strong>
            </button>
          ))}
        </section>
      </aside>
    </div>
  );
}

function Metadata({ label, value }: { label: string; value: string }) {
  return (
    <div className="metadata">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function App() {
  const [range, setRange] = useState<Range>("30d");
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VideoDetail | null>(null);
  const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null);
  const [groupDetail, setGroupDetail] = useState<ChannelGroupDetail | null>(null);
  const [sort, setSort] = useState<SortKey>("views");
  const [direction, setDirection] = useState<Direction>("desc");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [groupDetailLoading, setGroupDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dataRevision, setDataRevision] = useState(0);
  const refreshClicks = useRef(0);
  const refreshGap = useRef<number | null>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current !== null) {
      window.clearTimeout(toastTimer.current);
    }
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  };

  const loadDashboard = useCallback(async (options?: { quiet?: boolean }) => {
    if (!options?.quiet) {
      setLoading(true);
    }
    try {
      const data = await fetchJson<Dashboard>(`/api/dashboard?range=${range}`);
      setDashboard(data);
      setError(null);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : "Veriler yüklenemedi.");
    } finally {
      if (!options?.quiet) {
        setLoading(false);
      }
    }
  }, [range]);

  const loadVideos = useCallback(async (options?: { quiet?: boolean }) => {
    if (!options?.quiet) {
      setTableLoading(true);
    }
    try {
      const data = await fetchJson<Video[]>(`/api/videos?sort=${sort}&direction=${direction}`);
      setVideos(data);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : "Video tablosu yüklenemedi.");
    } finally {
      if (!options?.quiet) {
        setTableLoading(false);
      }
    }
  }, [direction, sort]);

  const runHiddenRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetch("/api/sync", {
        method: "POST",
        headers: { "x-playlist-pulse": "1" },
      });
      if (!response.ok) {
        throw new Error("sync failed");
      }
      const payload = (await response.json()) as { refreshed?: boolean };
      if (payload.refreshed !== false) {
        await Promise.all([loadDashboard({ quiet: true }), loadVideos({ quiet: true })]);
        setDataRevision((value) => value + 1);
      }
      showToast(payload.refreshed === false ? "Veri az önce güncellendi" : "Güncel veri çekildi");
    } catch {
      showToast("Veri çekilemedi");
    } finally {
      setRefreshing(false);
    }
  };

  const handleFooterMark = () => {
    if (refreshing) {
      return;
    }
    refreshClicks.current += 1;
    if (refreshGap.current !== null) {
      window.clearTimeout(refreshGap.current);
    }
    refreshGap.current = window.setTimeout(() => {
      refreshClicks.current = 0;
    }, 12000);
    if (refreshClicks.current < 10) {
      return;
    }
    refreshClicks.current = 0;
    if (refreshGap.current !== null) {
      window.clearTimeout(refreshGap.current);
    }
    void runHiddenRefresh();
  };

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    void loadVideos();
  }, [loadVideos]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    void fetchJson<VideoDetail>(`/api/videos/${encodeURIComponent(selectedId)}?range=${range}`)
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setError(fetchError instanceof Error ? fetchError.message : "Video detayı yüklenemedi.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataRevision, range, selectedId]);

  useEffect(() => {
    if (!selectedGroupName) {
      setGroupDetail(null);
      return;
    }
    let cancelled = false;
    setGroupDetailLoading(true);
    void fetchJson<ChannelGroupDetail>(
      `/api/groups/${encodeURIComponent(selectedGroupName)}?range=${range}`,
    )
      .then((data) => {
        if (!cancelled) {
          setGroupDetail(data);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setError(fetchError instanceof Error ? fetchError.message : "Kanal / albüm detayı yüklenemedi.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGroupDetailLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataRevision, range, selectedGroupName]);

  const filteredVideos = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("tr-TR");
    return normalizedQuery
      ? videos.filter((video) =>
          `${video.title} ${video.channelTitle ?? ""}`.toLocaleLowerCase("tr-TR").includes(normalizedQuery),
        )
      : videos;
  }, [query, videos]);

  const timelineData = useMemo(
    () =>
      (dashboard?.timeline ?? []).map((snapshot) => ({
        ...snapshot,
        playlistItemCount: snapshot.playlistItemCount || snapshot.videoCount,
        label: shortDateFormatter.format(new Date(snapshot.capturedAt)),
      })),
    [dashboard],
  );
  const topData = useMemo(
    () =>
      (dashboard?.topVideos ?? []).map((video) => ({
        title: shortTitle(video.title, 22),
        views: video.latest?.viewCount ?? 0,
      })),
    [dashboard],
  );
  const subscriberChannels = dashboard?.channels ?? [];
  const subscriberTimeline = useMemo(() => {
    const points = new Map<string, Record<string, number | string | null>>();
    for (const channel of subscriberChannels) {
      for (const point of channel.history) {
        const row = points.get(point.capturedAt) ?? {
          capturedAt: point.capturedAt,
          label: shortDateFormatter.format(new Date(point.capturedAt)),
        };
        row[channel.id] = point.subscriberCount;
        points.set(point.capturedAt, row);
      }
    }
    return [...points.values()].sort((left, right) => String(left.capturedAt).localeCompare(String(right.capturedAt)));
  }, [subscriberChannels]);
  const channelGroups = useMemo(() => groupVideosByChannel(videos), [videos]);
  const channelChartData = useMemo(
    () =>
      channelGroups.slice(0, 10).map((group) => ({
        name: shortTitle(group.name, 24),
        views: group.totalViews,
        likes: group.totalLikes,
      })),
    [channelGroups],
  );

  const changeSort = (nextSort: SortKey) => {
    if (nextSort === sort) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSort(nextSort);
    setDirection(nextSort === "rank" || nextSort === "position" || nextSort === "title" || nextSort === "added" ? "asc" : "desc");
  };

  const openVideoDetail = (videoId: string) => {
    setSelectedGroupName(null);
    setSelectedId(videoId);
  };

  const openGroupDetail = (groupName: string) => {
    setSelectedId(null);
    setSelectedGroupName(groupName);
  };

  const latest = dashboard?.latest;
  const baseline = dashboard?.baseline;
  const playlistTitle = dashboard?.playlist?.title || "Playlist Pulse";

  return (
    <main className="app-shell">
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />

      <header className="app-header">
        <a className="brand" href="/" aria-label="Playlist Pulse ana sayfa">
          <span className="brand__mark">
            <BarChart3 size={20} />
          </span>
          <span>Playlist Pulse</span>
        </a>
        <div className="header-actions">
          <span className={`sync-status sync-status--${dashboard?.latestRun?.status ?? "unknown"}`}>
            <span />
            {dashboard?.latestRun?.status === "failed"
              ? "Senkronizasyon hatası"
              : dashboard?.latestRun?.status === "running"
                ? "Veri toplanıyor"
                : "Canlı takip"}
          </span>
          <button className="icon-button" type="button" onClick={() => void loadDashboard()} aria-label="Verileri yeniden yükle">
            <RefreshCw size={18} className={loading ? "spin" : ""} />
          </button>
        </div>
      </header>

      <section className="hero">
        <div>
          <p className="eyebrow">YOUTUBE PLAYLIST ANALİTİĞİ</p>
          <h1>{playlistTitle}</h1>
          <p className="hero__subtitle">
            {dashboard?.playlist?.channelTitle || "Playlist metrikleri"} · Her video için tarihsel izlenme, etkileşim ve sıralama görünümü
          </p>
        </div>
        <div className="hero__meta">
          <span>
            <Clock3 size={15} />
            {dashboard?.playlist?.lastSyncedAt
              ? `Son veri: ${formatDate(dashboard.playlist.lastSyncedAt, true)}`
              : "İlk veri noktası bekleniyor"}
          </span>
          <span>
            <RefreshCw size={15} />
            {dashboard?.syncIntervalHours ?? 6} saatte bir güncellenir
          </span>
        </div>
      </section>

      <nav className="range-tabs" aria-label="Zaman aralığı">
        {(Object.keys(rangeLabels) as Range[]).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setRange(item)}
            className={range === item ? "is-active" : ""}
          >
            {rangeLabels[item]}
          </button>
        ))}
      </nav>

      {error && (
        <section className="error-banner">
          <strong>Veri kaynağı hazır değil.</strong>
          <span>{error}</span>
          <button type="button" onClick={() => void loadDashboard()}>
            Tekrar dene
          </button>
        </section>
      )}

      <section className="metric-grid">
        <MetricCard
          label="Toplam izlenme"
          value={latest?.totalViews}
          delta={difference(latest?.totalViews, baseline?.totalViews)}
          icon={<Eye size={18} />}
          tone="violet"
        />
        <MetricCard
          label="Playlist videoları"
          value={latest?.playlistItemCount ?? dashboard?.playlist?.itemCount ?? latest?.videoCount}
          delta={difference(latest?.playlistItemCount, baseline?.playlistItemCount)}
          icon={<ListMusic size={18} />}
          tone="cyan"
        />
        <MetricCard
          label="Toplam beğeni"
          value={latest?.totalLikes}
          delta={difference(latest?.totalLikes, baseline?.totalLikes)}
          icon={<Heart size={18} />}
          tone="pink"
        />
        <MetricCard
          label="Toplam yorum"
          value={latest?.totalComments}
          delta={difference(latest?.totalComments, baseline?.totalComments)}
          icon={<MessageCircle size={18} />}
          tone="amber"
        />
      </section>

      <section className="chart-grid">
        <article className="panel panel--wide">
          <div className="panel__header">
            <div>
              <p className="eyebrow">TARİHSEL TOPLAM</p>
              <h2>Playlist izlenme eğrisi</h2>
            </div>
            <span className="panel__stat">
              <TrendingUp size={16} />
              {formatNumber(difference(latest?.totalViews, baseline?.totalViews), true)}
            </span>
          </div>
          {timelineData.length ? (
            <ResponsiveContainer width="100%" height={285}>
              <AreaChart data={timelineData} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="playlistViews" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.48} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={32} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis tickFormatter={(value) => formatNumber(value, true)} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Area type="monotone" dataKey="totalViews" name="İzlenme" stroke="#a78bfa" fill="url(#playlistViews)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="İlk senkronizasyon tamamlandığında eğri burada görünür." />
          )}
        </article>

        <article className="panel">
          <div className="panel__header">
            <div>
              <p className="eyebrow">LİDERLİK TABLOSU</p>
              <h2>En çok izlenenler</h2>
            </div>
          </div>
          {topData.length ? (
            <ResponsiveContainer width="100%" height={285}>
              <BarChart data={topData} layout="vertical" margin={{ top: 4, right: 16, left: 10, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="title" type="category" width={120} tickLine={false} axisLine={false} tick={{ fill: "#b8c0d3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Bar dataKey="views" name="İzlenme" fill="#22d3ee" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Video verileri geldikten sonra liderlik listesi oluşur." />
          )}
        </article>
      </section>

      <section className="history-grid">
        <article className="panel panel--wide">
          <div className="panel__header">
            <div>
              <p className="eyebrow">TARİHSEL ETKİLEŞİM</p>
              <h2>Toplam beğeni ve yorum eğrisi</h2>
            </div>
            <span className="panel__stat panel__stat--engagement">
              <Heart size={15} />
              {formatNumber(difference(latest?.totalLikes, baseline?.totalLikes), true)}
            </span>
          </div>
          {timelineData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={timelineData} margin={{ top: 12, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={32} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis
                  yAxisId="likes"
                  tickFormatter={(value) => formatNumber(value, true)}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#f9a8d4", fontSize: 11 }}
                />
                <YAxis
                  yAxisId="comments"
                  orientation="right"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#fcd34d", fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Line yAxisId="likes" type="monotone" dataKey="totalLikes" name="Beğeni" stroke="#f472b6" strokeWidth={2.5} dot={{ r: 3, fill: "#f472b6" }} />
                <Line yAxisId="comments" type="monotone" dataKey="totalComments" name="Yorum" stroke="#fbbf24" strokeWidth={2.5} dot={{ r: 3, fill: "#fbbf24" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="İlk senkronizasyondan sonra etkileşim geçmişi görünür." />
          )}
        </article>

        <article className="panel">
          <div className="panel__header">
            <div>
              <p className="eyebrow">PLAYLIST BÜYÜMESİ</p>
              <h2>Playlist video sayısı</h2>
            </div>
            <span className="panel__stat panel__stat--count">
              <ListMusic size={15} />
              {formatNumber(latest?.playlistItemCount ?? latest?.videoCount)}
            </span>
          </div>
          {timelineData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={timelineData} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="playlistItems" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
                <XAxis dataKey="label" minTickGap={32} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Area type="stepAfter" dataKey="playlistItemCount" name="Playlist videoları" stroke="#22d3ee" fill="url(#playlistItems)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Playlist öğe sayısı, her senkronizasyonda kaydedilir." />
          )}
        </article>
      </section>

      <section className="panel subscriber-panel">
        <div className="panel__header">
          <div>
            <p className="eyebrow">KANAL ABONELERİ</p>
            <h2>Abone sayısı geçmişi</h2>
          </div>
          <span className="panel__stat panel__stat--count">
            <Users size={15} />
            {formatNumber(subscriberChannels.length)} kanal
          </span>
        </div>
        {subscriberTimeline.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={subscriberTimeline} margin={{ top: 12, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" vertical={false} />
              <XAxis dataKey="label" minTickGap={32} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} />
              <YAxis tickFormatter={(value) => formatNumber(value, true)} tickLine={false} axisLine={false} tick={{ fill: "#8f9bb3", fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(value) => formatNumber(value === null || value === undefined ? null : Number(value))} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              {subscriberChannels.map((channel, index) => (
                <Line
                  key={channel.id}
                  type="monotone"
                  dataKey={channel.id}
                  name={channel.title}
                  stroke={channelColors[index % channelColors.length]}
                  strokeWidth={2.4}
                  dot={{ r: 3 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="Kanal abone sayıları, bir sonraki veri toplamasından itibaren burada birikir." />
        )}
        <div className="table-wrap">
          <table className="subscriber-table">
            <thead>
              <tr>
                <th>Kanal</th>
                <th className="numeric">Abone</th>
                <th className="numeric">Seçili aralık</th>
                <th className="numeric">Kanal izlenmesi</th>
                <th className="numeric">Kanal videosu</th>
              </tr>
            </thead>
            <tbody>
              {subscriberChannels.length ? (
                subscriberChannels.map((channel) => {
                  const first = channel.history.find((point) => point.subscriberCount !== null);
                  const delta = difference(channel.subscriberCount, first?.subscriberCount);
                  return (
                    <tr key={channel.id}>
                      <td>
                        <div className="album-cell">
                          {channel.thumbnailUrl ? (
                            <img className="album-cover subscriber-avatar" src={channel.thumbnailUrl} alt="" />
                          ) : (
                            <span className="album-cover album-cover--placeholder subscriber-avatar"><Disc3 size={18} /></span>
                          )}
                          <div>
                            <strong>{channel.title}</strong>
                            <span>{formatDate(channel.capturedAt, true)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="numeric">{channel.hiddenSubscriberCount ? "Gizli" : formatNumber(channel.subscriberCount)}</td>
                      <td className="numeric">{channel.hiddenSubscriberCount || delta === null ? "—" : formatNumber(delta)}</td>
                      <td className="numeric">{formatNumber(channel.viewCount)}</td>
                      <td className="numeric">{formatNumber(channel.videoCount)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={5} className="table-message">Abone verisi henüz yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="album-section">
        <div className="album-section__header">
          <div>
            <p className="eyebrow">KANAL / ALBÜM GRUPLARI</p>
            <h2>Kanal ve albüm bazında performans</h2>
          </div>
          <span className="panel__stat">
            <Disc3 size={15} />
            {formatNumber(channelGroups.length)} grup
          </span>
        </div>

        <article className="panel album-chart-panel">
          <div className="panel__header">
            <div>
              <p className="eyebrow">TOPLAM İZLENME</p>
              <h2>En çok izlenen kanal / albümler</h2>
            </div>
          </div>
          {channelChartData.length ? (
            <ResponsiveContainer width="100%" height={Math.max(260, channelChartData.length * 42)}>
              <BarChart data={channelChartData} layout="vertical" margin={{ top: 4, right: 20, left: 26, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" width={148} tickLine={false} axisLine={false} tick={{ fill: "#b8c0d3", fontSize: 11 }} />
                <Tooltip formatter={(value) => formatNumber(Number(value))} />
                <Bar dataKey="views" name="İzlenme" fill="#a78bfa" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Kanal / albüm grupları video verileriyle birlikte oluşur." />
          )}
        </article>

        <article className="panel album-table-panel">
          <div className="table-wrap">
            <table className="album-table">
              <thead>
                <tr>
                  <th>Kanal / albüm</th>
                  <th className="numeric">Video</th>
                  <th className="numeric">İzlenme</th>
                  <th className="numeric">Beğeni</th>
                  <th className="numeric">Yorum</th>
                  <th className="numeric">Video başına</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr><td colSpan={6} className="table-message">Kanal / albüm grupları hesaplanıyor…</td></tr>
                ) : channelGroups.length ? (
                  channelGroups.map((group) => (
                    <tr
                      key={group.name}
                      onClick={() => openGroupDetail(group.name)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openGroupDetail(group.name);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td>
                        <div className="album-cell">
                          <AlbumCover group={group} />
                          <div>
                            <strong>{group.name}</strong>
                            <span>İlk video: {group.firstVideoTitle} · Detay için tıklayın</span>
                          </div>
                        </div>
                      </td>
                      <td className="numeric">{formatNumber(group.videoCount)}</td>
                      <td className="numeric">{formatNumber(group.totalViews)}</td>
                      <td className="numeric">{formatNumber(group.totalLikes)}</td>
                      <td className="numeric">{formatNumber(group.totalComments)}</td>
                      <td className="numeric">{formatNumber(group.averageViews)}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={6} className="table-message">Henüz gruplandırılacak video yok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="panel table-panel">
        <div className="panel__header panel__header--table">
          <div>
            <p className="eyebrow">TÜM VİDEOLAR</p>
            <h2>Detaylı metrik tablosu</h2>
          </div>
          <label className="search-box">
            <span>Filtrele</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Şarkı veya kanal ara"
            />
          </label>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th><SortButton label="Video" sortKey="title" current={sort} direction={direction} onSort={changeSort} /></th>
                <th><SortButton label="Playlist tarihi" sortKey="added" current={sort} direction={direction} onSort={changeSort} /></th>
                <th><SortButton label="Konum" sortKey="position" current={sort} direction={direction} onSort={changeSort} align="right" /></th>
                <th><SortButton label="İzlenme" sortKey="views" current={sort} direction={direction} onSort={changeSort} align="right" /></th>
                <th><SortButton label="Beğeni" sortKey="likes" current={sort} direction={direction} onSort={changeSort} align="right" /></th>
                <th><SortButton label="Yorum" sortKey="comments" current={sort} direction={direction} onSort={changeSort} align="right" /></th>
                <th><SortButton label="Sıra" sortKey="rank" current={sort} direction={direction} onSort={changeSort} align="right" /></th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr><td colSpan={7} className="table-message">Video verileri yükleniyor…</td></tr>
              ) : filteredVideos.length ? (
                filteredVideos.map((video) => (
                  <tr key={video.id} onClick={() => openVideoDetail(video.id)} tabIndex={0}>
                    <td>
                      <div className="video-cell">
                        <VideoAvatar video={video} />
                        <div>
                          <div className="video-cell__title">
                            <strong>{video.title}</strong>
                            <a
                              className="video-open-link"
                              href={youtubeVideoUrl(video.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`${video.title} videosunu YouTube'da yeni sekmede aç`}
                              title="YouTube'da aç"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <ExternalLink size={13} />
                              <span>YouTube</span>
                            </a>
                          </div>
                          <span>{video.channelTitle || "Bilinmeyen kanal"} · {formatDuration(video.duration)}</span>
                        </div>
                      </div>
                    </td>
                    <td>{formatDate(video.playlistAddedAt)}</td>
                    <td className="numeric">{video.currentPlaylistPosition ? `#${video.currentPlaylistPosition}` : "—"}</td>
                    <td className="numeric">{formatNumber(video.latest?.viewCount)}</td>
                    <td className="numeric">{formatNumber(video.latest?.likeCount)}</td>
                    <td className="numeric">{formatNumber(video.latest?.commentCount)}</td>
                    <td className="numeric"><span className="rank-badge">{video.latest?.rankByViews ? `#${video.latest.rankByViews}` : "—"}</span></td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={7} className="table-message">Bu filtreyle eşleşen video yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <footer className="app-footer">
        <div>
          <span
            className={`footer-mark${refreshing ? " is-busy" : ""}`}
            onClick={handleFooterMark}
          >
            <Database size={15} className={refreshing ? "spin" : undefined} />
          </span>
          D1 zaman serisi · R2 ham API arşivi · Cloudflare Workers Cron
        </div>
        <div>
          YouTube Data API yalnızca herkese açık metrikleri sunar. Kanal sahibine özel Analytics raporları OAuth izni gerektirir.
        </div>
      </footer>

      {toast && <div className="toast" role="status">{toast}</div>}

      {detailLoading && selectedId && !detail && (
        <div className="detail-loading"><Activity size={22} className="spin" /> Video analizi yükleniyor…</div>
      )}
      {groupDetailLoading && selectedGroupName && !groupDetail && (
        <div className="detail-loading"><Activity size={22} className="spin" /> Kanal / albüm analizi yükleniyor…</div>
      )}
      {detail && <DetailPanel detail={detail} onClose={() => setSelectedId(null)} />}
      {groupDetail && (
        <AlbumDetailPanel
          detail={groupDetail}
          onClose={() => setSelectedGroupName(null)}
          onSelectVideo={openVideoDetail}
        />
      )}
    </main>
  );
}
